import { LazyLoader } from "@NextSkyTheme/lazy-load";
import * as NextSkyTheme from "@NextSkyTheme/global";
import { sortCollectionGridByPrice } from "@NextSkyTheme/collection-price-sort";

let isNavigating = false;
let lastPath = window.location.pathname;

function getSectionId() {
  const grid = document.querySelector("#product-grid[data-id]");
  return grid?.dataset?.id || "";
}

function getCollectionRootPath(pathname) {
  const normalized = normalizePath(pathname);
  const match = normalized.match(/^(\/collections\/[^/]+)/);
  return match ? match[1] : normalized;
}

function isTagOnlyNavigation(fromPath, toPath) {
  const from = normalizePath(fromPath);
  const to = normalizePath(toPath);
  return from !== to && getCollectionRootPath(from) === getCollectionRootPath(to);
}

function getBannerSectionId() {
  return document.getElementById("CollectionBanner")?.dataset?.sectionId || "";
}

function getPageBreadcrumbSectionId() {
  return document.getElementById("CollectionPageBreadcrumb")?.dataset?.sectionId || "";
}

function updateCollectionBanner(htmlRender) {
  const source = htmlRender.getElementById("CollectionBanner");
  const destination = document.getElementById("CollectionBanner");
  if (!source || !destination) return;

  destination.innerHTML = source.innerHTML;
}

function updatePageBreadcrumb(htmlRender) {
  const source = htmlRender.getElementById("CollectionPageBreadcrumb");
  const destination = document.getElementById("CollectionPageBreadcrumb");
  if (!source || !destination) return;

  destination.innerHTML = source.innerHTML;
}

function updateDocumentTitle(htmlRender) {
  const heading = htmlRender.querySelector(".heading-title")?.textContent?.trim();
  if (!heading) return;

  const shopSuffix = document.title.includes(" – ")
    ? document.title.split(" – ").slice(1).join(" – ")
    : document.title.includes(" | ")
      ? document.title.split(" | ").slice(1).join(" | ")
      : "";

  document.title = shopSuffix ? `${heading} – ${shopSuffix}` : heading;
}

function fetchSectionDocument(pathname, sectionId, search = "") {
  const extraQuery = search ? `&${search.slice(1)}` : "";
  return fetch(`${pathname}?section_id=${sectionId}${extraQuery}`).then((response) => {
    if (!response.ok) throw new Error("fetch failed");
    return response.text();
  });
}

function updateProductGrid(htmlRender) {
  const source = htmlRender.getElementById("CollectionGird");
  const destination = document.getElementById("CollectionGird");
  if (source && destination) {
    const minHeight = destination.offsetHeight;
    if (minHeight > 0) {
      destination.style.minHeight = `${minHeight}px`;
    }
    destination.innerHTML = source.innerHTML;
    sortCollectionGridByPrice();
    requestAnimationFrame(() => {
      destination.style.minHeight = "";
    });
  }
}

function updateSizePillsVisibility(htmlRender) {
  const source = htmlRender.querySelector(".section-main-collection");
  const destination = document.querySelector(".section-main-collection");
  if (!source || !destination) return;

  destination.classList.toggle(
    "hide-size-pills",
    source.classList.contains("hide-size-pills")
  );
}

function normalizePath(pathname) {
  return pathname.replace(/\/+$/, "") || "/";
}

function syncTopFilterActiveState() {
  const topBar = document.querySelector("#CollectionTopFilters .collection-top-filters");
  if (!topBar) return;

  const currentPath = normalizePath(window.location.pathname);
  const links = topBar.querySelectorAll(".collection-top-filters__option");
  let activeLink = null;

  links.forEach((link) => {
    try {
      const linkPath = normalizePath(new URL(link.href, window.location.origin).pathname);
      if (linkPath === currentPath) {
        activeLink = link;
      }
    } catch (_error) {
      /* ignore malformed href */
    }
  });

  if (!activeLink) {
    const parentUrl = topBar.dataset.collectionUrl;
    if (parentUrl) {
      try {
        const parentPath = normalizePath(new URL(parentUrl, window.location.origin).pathname);
        if (currentPath === parentPath) {
          activeLink = links[0] || null;
        }
      } catch (_error) {
        /* ignore malformed parent url */
      }
    }
  }

  links.forEach((link) => {
    link.classList.toggle("is-active", link === activeLink);
  });
}

function findBestPathMatch(links, currentPath) {
  let matchedLink = null;
  let matchedPathLength = 0;

  links.forEach((link) => {
    try {
      const linkPath = normalizePath(new URL(link.href, window.location.origin).pathname);
      const isMatch =
        currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
      if (isMatch && linkPath.length > matchedPathLength) {
        matchedLink = link;
        matchedPathLength = linkPath.length;
      }
    } catch (_error) {
      /* ignore malformed href */
    }
  });

  return matchedLink;
}

function isProductLinePath(currentPath, sidebar) {
  const productFilter = sidebar?.querySelector('[data-index^="custom-product-line"]');
  if (productFilter) {
    const productMatch = findBestPathMatch(
      productFilter.querySelectorAll(".custom-filter-option"),
      currentPath
    );
    if (productMatch) return true;
  }

  const topBar = document.querySelector("#CollectionTopFilters .collection-top-filters");
  if (topBar?.dataset?.topContext !== "product") return false;

  const parentUrl = topBar.dataset.collectionUrl;
  if (!parentUrl) return false;

  try {
    const parentPath = normalizePath(new URL(parentUrl, window.location.origin).pathname);
    return currentPath === parentPath || currentPath.startsWith(`${parentPath}/`);
  } catch (_error) {
    return false;
  }
}

function syncSidebarActiveState() {
  const sidebar = document.querySelector(".collection-custom-filters--sidebar");
  if (!sidebar) return;

  const currentPath = normalizePath(window.location.pathname);
  const activeLinks = new Set();

  const productFilter = sidebar.querySelector('[data-index^="custom-product-line"]');
  const concernFilter = sidebar.querySelector('[data-index^="custom-concern"]');

  const productMatch = productFilter
    ? findBestPathMatch(
        productFilter.querySelectorAll(".custom-filter-option"),
        currentPath
      )
    : null;

  const concernMatch = concernFilter
    ? findBestPathMatch(
        concernFilter.querySelectorAll(".custom-filter-option"),
        currentPath
      )
    : null;

  if (isProductLinePath(currentPath, sidebar)) {
    if (productMatch) activeLinks.add(productMatch);
  } else if (concernMatch) {
    activeLinks.add(concernMatch);
  } else if (productMatch) {
    activeLinks.add(productMatch);
  }

  sidebar.querySelectorAll(".custom-filter-option").forEach((link) => {
    link.classList.toggle("is-active", activeLinks.has(link));
  });
}

function syncCustomFilterActiveStates() {
  syncTopFilterActiveState();
  syncSidebarActiveState();
}

function updateTopFilters(htmlRender, { tagOnly = false } = {}) {
  if (tagOnly) return;

  const source = htmlRender.getElementById("CollectionTopFilters");
  const destination = document.getElementById("CollectionTopFilters");
  if (!destination) return;

  const nextHtml = source ? source.innerHTML : "";
  if (nextHtml !== destination.innerHTML) {
    destination.innerHTML = nextHtml;
  }
}

function updateFilters(htmlRender, { tagOnly = false } = {}) {
  if (tagOnly) return;

  ["FacetFiltersForm", "FacetFiltersFormDrawer"].forEach((formId) => {
    htmlRender.querySelectorAll(`#${formId} .js-filter`).forEach((source) => {
      if (source.classList.contains("js-custom-filter")) return;

      const target = document.querySelector(
        `#${formId} .js-filter[data-index="${source.dataset.index}"]`
      );
      if (target) target.innerHTML = source.innerHTML;
    });
  });
}

function updateProductCount(htmlRender) {
  ["collection-count", "facets-filters-active", "facets-filters-drawer-active", "pagination-load-more"].forEach(
    (blockClass) => {
      const source = htmlRender.querySelector(`.${blockClass}`);
      const destination = document.querySelector(`.${blockClass}`);
      if (source && destination) destination.innerHTML = source.innerHTML;
    }
  );
}

function navigateToUrl(targetUrl) {
  const target = new URL(targetUrl, window.location.origin);
  const sectionId = getSectionId();

  if (!sectionId) {
    window.location.href = target.href;
    return;
  }

  const fromPath = normalizePath(window.location.pathname);
  const toPath = normalizePath(target.pathname);
  const tagOnly = isTagOnlyNavigation(fromPath, toPath);
  const extraQuery = target.search ? `&${target.search.slice(1)}` : "";
  const gridFetchUrl = `${target.pathname}?section_id=${sectionId}${extraQuery}`;
  const bannerSectionId = tagOnly ? "" : getBannerSectionId();
  const breadcrumbSectionId = tagOnly ? "" : getPageBreadcrumbSectionId();
  const container = document.getElementById("ProductsGridContainer");

  isNavigating = true;
  if (container) {
    if (tagOnly) {
      container.classList.add("is-filter-updating");
    } else {
      container.classList.add("loading");
      NextSkyTheme.getBody().classList.add("loading");
    }
  }

  const sectionFetches = {
    grid: fetch(gridFetchUrl).then((response) => {
      if (!response.ok) throw new Error("fetch failed");
      return response.text();
    }),
  };

  if (bannerSectionId) {
    sectionFetches.banner = fetchSectionDocument(
      target.pathname,
      bannerSectionId,
      target.search
    ).catch(() => "");
  }

  if (breadcrumbSectionId) {
    sectionFetches.breadcrumb = fetchSectionDocument(
      target.pathname,
      breadcrumbSectionId,
      target.search
    ).catch(() => "");
  }

  Promise.all([
    sectionFetches.grid,
    sectionFetches.banner || Promise.resolve(""),
    sectionFetches.breadcrumb || Promise.resolve(""),
  ])
    .then(([gridHtml, bannerHtml = "", breadcrumbHtml = ""]) => {
      const htmlRender = new DOMParser().parseFromString(gridHtml, "text/html");

      // If the fetched collection doesn't render the expected grid section
      // (e.g. it uses a different template), fall back to a full navigation.
      if (!htmlRender.getElementById("CollectionGird")) {
        window.location.href = target.href;
        return;
      }

      updateProductGrid(htmlRender);
      updateSizePillsVisibility(htmlRender);
      updateFilters(htmlRender, { tagOnly });
      updateTopFilters(htmlRender, { tagOnly });
      updateProductCount(htmlRender);

      if (bannerHtml) {
        const bannerRender = new DOMParser().parseFromString(bannerHtml, "text/html");
        updateCollectionBanner(bannerRender);
        updateDocumentTitle(bannerRender);
      }

      if (breadcrumbHtml) {
        const breadcrumbRender = new DOMParser().parseFromString(breadcrumbHtml, "text/html");
        updatePageBreadcrumb(breadcrumbRender);
      }

      history.pushState({ customFilters: true }, "", target.href);
      lastPath = target.pathname;
      window.dispatchEvent(new CustomEvent("collection-filters:navigated"));
      new LazyLoader(".image-lazy-load");
      syncCustomFilterActiveStates();
    })
    .catch(() => {
      window.location.href = target.href;
    })
    .finally(() => {
      isNavigating = false;
      if (container) {
        container.classList.remove("loading", "is-filter-updating");
      }
      NextSkyTheme.getBody().classList.remove("loading");
    });
}

function onFilterClick(event) {
  const link = event.target.closest(".custom-filter-option");
  if (!link || isNavigating) return;
  if (!link.closest(".collection-custom-filters")) return;

  event.preventDefault();
  event.stopPropagation();

  navigateToUrl(link.href);
}

function onPopState() {
  // Only handle history entries where the collection path changed (our custom
  // filters). Query-only changes (native price/sort filters) are handled by
  // facets.js, so we ignore them here to avoid double-fetching.
  if (window.location.pathname === lastPath) return;
  lastPath = window.location.pathname;
  navigateToUrl(window.location.href);
}

function initCollectionCustomFilters() {
  if (document.body.dataset.collectionCustomFiltersBound) return;
  document.body.dataset.collectionCustomFiltersBound = "true";
  document.addEventListener("click", onFilterClick, true);
  window.addEventListener("popstate", onPopState);
  syncCustomFilterActiveStates();
}

initCollectionCustomFilters();

window.CollectionCustomFilters = { navigateToUrl };
